import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { SettingsShell } from "./SettingsShell";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function SubscriptionSettings() {
    const { user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [sub, setSub] = useState(null);
    const [busy, setBusy] = useState(false);
    const [sp] = useSearchParams();
    const isPlaceholderConfirm = sp.get("placeholder") === "1";
    const planToConfirm = sp.get("plan");

    const load = async () => {
        try {
            const [pRes, sRes] = await Promise.all([api.get("/plans"), api.get("/subscriptions/current")]);
            setPlans(pRes.data);
            setSub(sRes.data);
        } catch (err) {
            toast.error("Could not load subscription");
        }
    };

    useEffect(() => { load(); }, []);

    const upgrade = async (plan_key) => {
        setBusy(true);
        try {
            const { data } = await api.post("/subscriptions/checkout", { plan_key });
            if (data.mode === "stripe_test" && data.checkout_url?.startsWith("http")) {
                window.location.href = data.checkout_url;
            } else {
                await api.post("/subscriptions/confirm-placeholder", { plan_key });
                toast.success(`Upgraded to ${plan_key} (placeholder billing)`);
                load();
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upgrade failed");
        } finally {
            setBusy(false);
        }
    };

    const extraTabs = user?.is_super_admin ? [{ to: "/app/settings/software-branding", label: "Software Branding" }] : [];

    return (
        <SettingsShell title="Subscription & billing" subtitle={sub?.status === "placeholder" ? "Billing is in placeholder mode. Plan selections persist so entitlements work end-to-end." : "Manage your plan."} extraTabs={extraTabs}>
            {isPlaceholderConfirm && (
                <div className="mb-6 border border-border p-4 bg-secondary/50 text-sm" data-testid="placeholder-banner">
                    <strong>Billing Integration — Test Mode Placeholder.</strong> Plan {planToConfirm ? <span className="font-mono">{planToConfirm}</span> : ""} was selected without a live Stripe key. A production Stripe test key can be added to <span className="font-mono">STRIPE_SECRET_KEY</span> to enable real checkout.
                </div>
            )}
            <div className="mb-8 border border-border p-6" data-testid="current-plan-card">
                <div className="overline">Current plan</div>
                <div className="mt-2 font-display text-3xl tracking-tight capitalize">{sub?.plan_key || "—"}</div>
                <div className="text-xs text-muted-foreground mt-1 capitalize">{sub?.status || ""}</div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 border border-border" data-testid="plans-grid">
                {plans.map((p, i) => {
                    const isCurrent = sub?.plan_key === p.key;
                    return (
                        <div key={p.key} className="p-6 border-r border-b border-border last:border-r-0 [&:nth-child(2n)]:border-r-0 md:[&:nth-child(2n)]:border-r lg:[&:nth-child(5n)]:border-r-0 lg:[&:nth-child(n)]:border-r" data-testid={`sub-plan-${p.key}`}>
                            <div className="overline">{p.name}</div>
                            <div className="mt-3 font-display text-3xl tracking-tight leading-none">
                                {p.price_monthly === 0 ? "Talk" : `$${p.price_monthly}`}
                                {p.price_monthly > 0 && <span className="text-xs text-muted-foreground font-sans font-normal"> /mo</span>}
                            </div>
                            <div className="mt-4 text-xs text-muted-foreground">
                                <div>{p.limits.users === -1 ? "Unlimited" : p.limits.users} users</div>
                                <div>{p.limits.projects === -1 ? "Unlimited" : p.limits.projects} projects</div>
                            </div>
                            <ul className="mt-4 space-y-1 text-xs">
                                {p.features.slice(0, 6).map((f) => (
                                    <li key={f} className="flex items-start gap-2"><Check size={10} className="mt-1 text-navy" /><span>{f.replace(/_/g, " ")}</span></li>
                                ))}
                            </ul>
                            <button
                                disabled={busy || isCurrent}
                                onClick={() => upgrade(p.key)}
                                className={`mt-6 w-full ${isCurrent ? "border border-border text-muted-foreground cursor-not-allowed" : i === 2 ? "bg-navy text-arch-white" : "border border-border hover:bg-secondary"} px-4 py-2 text-sm disabled:opacity-70`}
                                data-testid={`sub-cta-${p.key}`}
                            >
                                {isCurrent ? "Current plan" : p.key === "enterprise" ? "Contact Sales" : "Select"}
                            </button>
                        </div>
                    );
                })}
            </div>
        </SettingsShell>
    );
}
